import { useState, useCallback, useEffect } from 'react'
import { Pencil, Trash2, Sparkles, Loader2 } from 'lucide-react'
import toast from 'react-hot-toast'
import Modal from '@/components/common/Modal'
import { listFunctionalRoles } from '@/api/team'
import { suggestRole } from '@/api/designers'
import { parseApiError } from '@/livepreview/utils'
import type { Participant } from './types'

interface Props {
  open: boolean
  onClose: () => void
  participants: Participant[]
  onAdd: (p: Participant) => void
  onUpdate: (index: number, p: Participant) => void
  onDelete: (index: number) => boolean
  /** Project folder name — used to suggest the tenant's functional (approver)
   *  roles in the Role Code field. */
  project?: string
}

const emptyForm: Participant = {
  id: '',
  name: '',
  type: 'ROLE',
  roleCode: '',
  participatesFor: '',
  roleType: 'Static',
  roleExpression: '',
}

const ROLE_LIST_ID = 'wf-participant-functional-roles'

export default function ParticipantModal({
  open,
  onClose,
  participants,
  onAdd,
  onUpdate,
  onDelete,
  project,
}: Props) {
  const [form, setForm] = useState<Participant>({ ...emptyForm })
  const [editIndex, setEditIndex] = useState<number | null>(null)
  const [roleOptions, setRoleOptions] = useState<string[]>([])
  // "Define a role with AI" — a description and its in-flight flag.
  const [roleDesc, setRoleDesc] = useState('')
  const [roleBusy, setRoleBusy] = useState(false)

  // Reset form when modal opens
  useEffect(() => {
    if (open) {
      setForm({ ...emptyForm })
      setEditIndex(null)
      setRoleDesc('')
    }
  }, [open])

  // Load the project's functional (approver) roles for the datalist.
  useEffect(() => {
    if (!open || !project) return
    let cancelled = false
    listFunctionalRoles(project)
      .then(res => { if (!cancelled) setRoleOptions(res.roles || []) })
      .catch(() => { /* non-fatal — the field stays free-text */ })
    return () => { cancelled = true }
  }, [open, project])

  const handleSubmit = useCallback((e: React.FormEvent) => {
    e.preventDefault()
    if (!form.id || !form.name || !form.type) {
      toast.error('Please fill in all required fields')
      return
    }

    if (editIndex !== null) {
      onUpdate(editIndex, { ...form })
      toast.success('Participant updated')
    } else {
      // Check duplicate ID
      if (participants.some(p => p.id === form.id)) {
        toast.error('Participant ID already exists')
        return
      }
      onAdd({ ...form })
      toast.success('Participant added')
    }

    setForm({ ...emptyForm })
    setEditIndex(null)
  }, [form, editIndex, participants, onAdd, onUpdate])

  const handleEdit = useCallback((index: number) => {
    const p = participants[index]
    setForm({ ...p })
    setEditIndex(index)
  }, [participants])

  const handleDelete = useCallback((index: number) => {
    const success = onDelete(index)
    if (!success) {
      toast.error('Cannot delete: participant is used in the workflow')
    } else {
      toast.success('Participant deleted')
    }
  }, [onDelete])

  const handleTypeChange = useCallback((type: 'ROLE' | 'SYSTEM') => {
    setForm(prev => ({
      ...prev,
      type,
      roleCode: type === 'ROLE' ? prev.roleCode : '',
      participatesFor: type === 'ROLE' ? prev.participatesFor : '',
      roleType: type === 'ROLE' ? prev.roleType : '',
      roleExpression: type === 'ROLE' ? prev.roleExpression : '',
    }))
  }, [])

  // Define an approver role from a plain-language description (reuses an
  // existing project role when it fits). Fills Role Code, and the Name when blank.
  const handleSuggestRole = useCallback(async () => {
    const desc = roleDesc.trim() || form.name.trim()
    if (!project) { toast.error('Open the workflow from a project to use AI.'); return }
    if (!desc) { toast.error('Describe the approver first (or fill the Name).'); return }
    setRoleBusy(true)
    try {
      const res = await suggestRole({
        project, description: desc, existing_roles: roleOptions,
        participant_name: form.name,
      })
      setForm(f => ({
        ...f,
        roleCode: res.role_code,
        name: f.name.trim() ? f.name : res.name,
        id: f.id.trim() ? f.id : res.role_code,
      }))
      toast.success(res.reused ? `Reused role "${res.role_code}"` : `Defined role "${res.role_code}"`)
    } catch (err) {
      toast.error(`Could not suggest a role: ${parseApiError(err).message}`)
    } finally {
      setRoleBusy(false)
    }
  }, [project, roleDesc, form.name, form.id, roleOptions])

  return (
    <Modal open={open} onClose={onClose} title="Manage Participants" size="wide">
      <div style={{ fontSize: '0.8625rem', color: 'var(--color-text-secondary)', marginBottom: 12 }}>
        Participants are people or systems which perform various activities in the workflow.
      </div>

      {/* Participant list */}
      {participants.length > 0 && (
        <div style={{ marginBottom: 16, maxHeight: 200, overflowY: 'auto' }}>
          {participants.map((p, i) => (
            <div key={p.id} style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '6px 8px',
              borderBottom: '1px solid var(--color-border)',
              fontSize: '0.8625rem',
            }}>
              <span style={{ color: 'var(--color-text)' }}>
                {p.name} <span style={{ color: 'var(--color-text-secondary)' }}>({p.type})</span>
              </span>
              <div style={{ display: 'flex', gap: 4 }}>
                <button onClick={() => handleEdit(i)} className="btn btn-ghost btn-sm" title="Edit">
                  <Pencil size={13} />
                </button>
                <button onClick={() => handleDelete(i)} className="btn btn-ghost btn-sm" title="Delete">
                  <Trash2 size={13} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add/Edit form */}
      <form onSubmit={handleSubmit}>
        <h4 style={{ margin: '0 0 8px', fontSize: '0.9125rem', fontWeight: 600, color: 'var(--color-text)' }}>
          {editIndex !== null ? 'Edit Participant' : 'Add New Participant'}
        </h4>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
          <FormField label="ID" value={form.id} onChange={v => setForm(f => ({ ...f, id: v }))} required />
          <FormField label="Name" value={form.name} onChange={v => setForm(f => ({ ...f, name: v }))} required />
        </div>

        <div style={{ marginTop: 8 }}>
          <label style={labelStyle}>Type</label>
          <select
            value={form.type}
            onChange={e => handleTypeChange(e.target.value as 'ROLE' | 'SYSTEM')}
            style={inputStyle}
            required
          >
            <option value="">Select Type</option>
            <option value="ROLE">ROLE</option>
            <option value="SYSTEM">SYSTEM</option>
          </select>
        </div>

        {form.type === 'ROLE' && (
          <div style={{ marginTop: 8 }}>
            {roleOptions.length > 0 && (
              <datalist id={ROLE_LIST_ID}>
                {roleOptions.map(r => <option key={r} value={r} />)}
              </datalist>
            )}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
              <FormField label="Role Code" value={form.roleCode || ''} onChange={v => setForm(f => ({ ...f, roleCode: v }))} list={roleOptions.length > 0 ? ROLE_LIST_ID : undefined} />
              <FormField label="Participates For" value={form.participatesFor || ''} onChange={v => setForm(f => ({ ...f, participatesFor: v }))} />
            </div>

            {/* Define a role with AI — right next to the role selection */}
            <div style={{ display: 'flex', gap: 6, alignItems: 'center', marginTop: 6 }}>
              <input
                value={roleDesc}
                onChange={e => setRoleDesc(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); void handleSuggestRole() } }}
                placeholder="Describe the approver → AI suggests a role code"
                style={{ ...inputStyle, flex: 1 }}
              />
              <button
                type="button"
                className="btn btn-ghost btn-sm"
                disabled={roleBusy}
                onClick={() => void handleSuggestRole()}
                title="Define an approver role from a description with AI"
                style={{ flexShrink: 0, display: 'inline-flex', alignItems: 'center', gap: 4 }}
              >
                {roleBusy
                  ? <Loader2 size={13} style={{ animation: 'spin 0.6s linear infinite' }} />
                  : <Sparkles size={13} />}
                Define with AI
              </button>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginTop: 8 }}>
              <div>
                <label style={labelStyle}>Role Type</label>
                <select
                  value={form.roleType || 'Static'}
                  onChange={e => setForm(f => ({ ...f, roleType: e.target.value }))}
                  style={inputStyle}
                >
                  <option value="Static">Static</option>
                  <option value="Dynamic">Dynamic</option>
                </select>
              </div>
              <FormField label="Role Expression" value={form.roleExpression || ''} onChange={v => setForm(f => ({ ...f, roleExpression: v }))} />
            </div>
          </div>
        )}

        <button type="submit" className="btn btn-primary btn-sm" style={{ marginTop: 12, width: '100%' }}>
          {editIndex !== null ? 'Update Participant' : 'Add Participant'}
        </button>
      </form>
    </Modal>
  )
}

function FormField({
  label, value, onChange, required, list,
}: {
  label: string; value: string; onChange: (v: string) => void; required?: boolean; list?: string
}) {
  return (
    <div>
      <label style={labelStyle}>{label}</label>
      <input
        value={value}
        onChange={e => onChange(e.target.value)}
        required={required}
        list={list}
        style={inputStyle}
        placeholder={label}
      />
    </div>
  )
}

const labelStyle: React.CSSProperties = {
  display: 'block',
  fontSize: '0.8125rem',
  color: 'var(--color-text-secondary)',
  marginBottom: 2,
  fontWeight: 500,
}

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '5px 8px',
  fontSize: '0.8625rem',
  background: 'var(--color-bg-secondary)',
  border: '1px solid var(--color-border)',
  borderRadius: 4,
  color: 'var(--color-text)',
  outline: 'none',
  boxSizing: 'border-box',
}
